import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Zap, Info } from 'lucide-react';
import { api } from '../lib/api';

const MOTIFS_LABELS = {
  // Legacy
  EXONERATION: { label: 'Exonération', color: 'bg-emerald-100 text-emerald-700', taux: '0 €/MWh' },
  ELECTRO_INTENSIVE: { label: 'Électro-intensif', color: 'bg-blue-100 text-blue-700', taux: 'selon ratio' },
  FUITE_CARBONE: { label: 'Fuite de carbone', color: 'bg-amber-100 text-amber-700', taux: 'selon ratio' },
  QUOTAS_CO2: { label: 'Quotas CO2', color: 'bg-orange-100 text-orange-700', taux: 'selon ratio' },
  CONCURRENCE_INTERNATIONALE: { label: 'Concurrence int.', color: 'bg-orange-100 text-orange-700', taux: 'selon ratio' },
  DESHYDRATATION_LEGUMES: { label: 'Déshydratation', color: 'bg-orange-100 text-orange-700', taux: 'selon ratio' },
  CARBURANT: { label: 'Carburant', color: 'bg-blue-100 text-blue-700', taux: 'réduit' },
  TRAVAUX_AGRICOLES: { label: 'Travaux agricoles', color: 'bg-green-100 text-green-700', taux: 'réduit' },
  // Sous-motifs EXONÉRATION (Phase 1 TICGN)
  EXO_USAGE_NON_COMBUSTIBLE: { label: 'Exo. – Usage non combustible/carburant', color: 'bg-emerald-100 text-emerald-700', taux: '0 €/MWh' },
  EXO_DOUBLE_USAGE: { label: 'Exo. – Double usage (matières premières)', color: 'bg-emerald-100 text-emerald-700', taux: '0 €/MWh' },
  EXO_MINERAL_NON_METALLIQUE: { label: 'Exo. – Produits minéraux non métalliques', color: 'bg-emerald-100 text-emerald-700', taux: '0 €/MWh' },
  EXO_PROD_PRODUITS_ENERGETIQUES: { label: 'Exo. – Production produits énergétiques', color: 'bg-emerald-100 text-emerald-700', taux: '0 €/MWh' },
  EXO_PROD_ELECTRICITE: { label: "Exo. – Production d'électricité", color: 'bg-emerald-100 text-emerald-700', taux: '0 €/MWh' },
  EXO_BIOGAZ_NON_INJECTE: { label: 'Exo. – Biogaz non injecté', color: 'bg-emerald-100 text-emerald-700', taux: '0 €/MWh' },
  EXO_NAVIGATION_INTERIEURE: { label: 'Exo. – Navigation intérieure', color: 'bg-emerald-100 text-emerald-700', taux: '0 €/MWh' },
  EXO_NAVIGATION_MARITIME: { label: 'Exo. – Navigation maritime', color: 'bg-emerald-100 text-emerald-700', taux: '0 €/MWh' },
  EXO_NAVIGATION_AERIENNE: { label: 'Exo. – Navigation aérienne', color: 'bg-emerald-100 text-emerald-700', taux: '0 €/MWh' },
};

// Motifs nécessitant un justificatif obligatoire (texte libre)
const MOTIFS_JUSTIFICATIF_OBLIGATOIRE = ['EXO_DOUBLE_USAGE'];

// Motifs nécessitant le choix d'une rubrique ICPE (sur la tranche)
const MOTIFS_ICPE_OBLIGATOIRE = ['EXO_MINERAL_NON_METALLIQUE'];

// Sous-motifs EXO toujours saisissables manuellement (cf. choix utilisateur 4.a)
const MOTIFS_EXO_LIBRES = [
  'EXO_USAGE_NON_COMBUSTIBLE',
  'EXO_DOUBLE_USAGE',
  'EXO_MINERAL_NON_METALLIQUE',
  'EXO_PROD_PRODUITS_ENERGETIQUES',
  'EXO_PROD_ELECTRICITE',
  'EXO_BIOGAZ_NON_INJECTE',
  'EXO_NAVIGATION_INTERIEURE',
  'EXO_NAVIGATION_MARITIME',
  'EXO_NAVIGATION_AERIENNE',
];

// Motifs TARIF RÉDUIT TICGN — sélectionnables librement par tranche (l'energy manager
// assume la justification). L'éligibilité reste soumise au ratio TICGN/VAC ≥ 0,5 %
// pour QUOTAS_CO2, CONCURRENCE_INTERNATIONALE, DESHYDRATATION_LEGUMES ; sans ratio
// pour CARBURANT et TRAVAUX_AGRICOLES.
const MOTIFS_TARIF_REDUIT_LIBRES = [
  'QUOTAS_CO2',
  'CONCURRENCE_INTERNATIONALE',
  'DESHYDRATATION_LEGUMES',
  'CARBURANT',
  'TRAVAUX_AGRICOLES',
];

const TranchesEligibilite = ({ pdl, pce, codeNaf, tranches = [], onChange, apiEndpoint = '/api/ticfe/naf', icpeMotif = null }) => {
  const [motifsDisponibles, setMotifsDisponibles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [icpeRubriques, setIcpeRubriques] = useState([]);
  const identifiantPoint = pdl || pce || '';
  const isTicgn = apiEndpoint.includes('ticgn');

  // Charger une seule fois la liste des rubriques ICPE (TICGN uniquement)
  useEffect(() => {
    if (!isTicgn) return;
    api.get('/ticgn/icpe-rubriques')
      .then(res => setIcpeRubriques(res.data?.rubriques || []))
      .catch(() => setIcpeRubriques([]));
  }, [isTicgn]);

  useEffect(() => {
    if (!codeNaf) return;
    setLoading(true);
    api.get(`${apiEndpoint.replace('/api/', '/')}/${codeNaf}/eligibilite`)
      .then(res => {
        const motifs = res.data?.motifs_disponibles || [];
        setMotifsDisponibles(motifs);

        // === Pré-remplissage automatique des tranches selon le motif typé du NAF ===
        // Mapping (mot-clé en eligibility_reason → motif). Le 1er mot-clé matché gagne.
        const reason = (res.data?.eligibility_reason || '').toLowerCase();
        const motifSet = new Set(motifs.map(m => m.motif));
        const AUTO_PREFILL_MAP = [
          { keywords: ['concurrence internationale', 'fuite de carbone'], motif: 'CONCURRENCE_INTERNATIONALE' },
          { keywords: ['quotas co2', 'seqe', 'ets'], motif: 'QUOTAS_CO2' },
          { keywords: ['déshydratation', 'deshydratation'], motif: 'DESHYDRATATION_LEGUMES' },
          { keywords: ['carburant'], motif: 'CARBURANT' },
          { keywords: ['travaux agricoles', 'agricole', 'forestier', 'sylviculture'], motif: 'TRAVAUX_AGRICOLES' },
        ];
        const matched = AUTO_PREFILL_MAP.find(({ keywords, motif }) =>
          motifSet.has(motif) && keywords.some(kw => reason.includes(kw))
        );
        if (matched && (!tranches || tranches.length === 0) && typeof onChange === 'function') {
          const labelDefault = (MOTIFS_LABELS[matched.motif]?.label || matched.motif);
          onChange([{
            part: 100,
            motif: matched.motif,
            description: `Auto : NAF éligible (${labelDefault})`,
            justificatif: '',
            auto_prefill: true,
          }]);
        }
      })
      .catch(() => setMotifsDisponibles([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeNaf, apiEndpoint]);

  // Compose la liste finale des motifs candidats : NAF + ICPE + sous-motifs EXO libres (TICGN)
  const isTicgnContext = isTicgn;
  const motifsLibres = isTicgnContext
    ? [...MOTIFS_EXO_LIBRES, ...MOTIFS_TARIF_REDUIT_LIBRES]
    : [];
  const tousLesMotifs = (() => {
    const set = new Map();
    motifsDisponibles.forEach(m => set.set(m.motif, { motif: m.motif, label: m.label || (MOTIFS_LABELS[m.motif]?.label) || m.motif }));
    if (icpeMotif && !set.has(icpeMotif)) {
      set.set(icpeMotif, {
        motif: icpeMotif,
        label: MOTIFS_LABELS[icpeMotif]?.label || icpeMotif,
        source: 'icpe',
      });
    }
    motifsLibres.forEach(m => {
      if (!set.has(m)) set.set(m, { motif: m, label: MOTIFS_LABELS[m]?.label || m, source: 'libre' });
    });
    return Array.from(set.values());
  })();

  const totalParts = tranches.reduce((sum, t) => sum + (parseFloat(t.part) || 0), 0);
  const restant = Math.max(0, 100 - totalParts);

  const updateTranche = (index, field, value) => {
    const updated = [...tranches];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeTranche = (index) => {
    onChange(tranches.filter((_, i) => i !== index));
  };

  if (tousLesMotifs.length === 0 && !loading) {
    return (
      <div className="text-xs text-[#687175] italic flex items-center gap-1">
        <Info className="w-3 h-3" />
        Code NAF non éligible — taux CERFA standard appliqué
      </div>
    );
  }

  const addTrancheLocal = () => {
    const defaultMotif = tousLesMotifs.length > 0 ? tousLesMotifs[0].motif : 'ELECTRO_INTENSIVE';
    onChange([...tranches, { part: restant > 0 ? Math.min(restant, 50) : 10, motif: defaultMotif, description: '', justificatif: '' }]);
  };

  return (
    <div className="space-y-2" data-testid={`tranches-${identifiantPoint}`}>
      {tranches.length === 0 && (
        <div className="text-xs text-[#687175] mb-1">
          100% de la consommation sera calculée avec le motif NAF par défaut.
        </div>
      )}

      {tranches.map((tranche, idx) => {
        const motifNeedsJustif = MOTIFS_JUSTIFICATIF_OBLIGATOIRE.includes(tranche.motif);
        const motifNeedsIcpe = MOTIFS_ICPE_OBLIGATOIRE.includes(tranche.motif);
        return (
        <div key={idx} className="bg-[#F5F6F9] rounded-lg p-2 space-y-1.5" data-testid={`tranche-row-${idx}`}>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              max="100"
              value={tranche.part}
              onChange={(e) => updateTranche(idx, 'part', Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
              className="w-16 px-2 py-1 border border-[#BFC6CC] rounded text-sm text-center"
              data-testid={`tranche-part-${idx}`}
            />
            <span className="text-xs text-[#687175]">%</span>
            <select
              value={tranche.motif}
              onChange={(e) => updateTranche(idx, 'motif', e.target.value)}
              className="flex-1 px-2 py-1 border border-[#BFC6CC] rounded text-sm bg-white"
              data-testid={`tranche-motif-${idx}`}
            >
              {tousLesMotifs.map(m => (
                <option key={m.motif} value={m.motif}>
                  {m.label}{m.source === 'icpe' ? ' (ICPE)' : ''}
                </option>
              ))}
            </select>
            <span className="text-xs text-[#687175] min-w-[60px]">
              {MOTIFS_LABELS[tranche.motif]?.taux || '—'}
            </span>
            <button
              type="button"
              onClick={() => removeTranche(idx)}
              className="p-1 text-red-500 hover:bg-red-50 rounded"
              data-testid={`tranche-remove-${idx}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          {motifNeedsJustif && (
            <div className="pl-1">
              <label className="block text-xs font-medium text-[#687175] mb-0.5">
                Justificatif <span className="text-red-500">*</span>
                <span className="text-[#687175] italic font-normal ml-1">(obligatoire pour ce motif)</span>
              </label>
              <textarea
                value={tranche.justificatif || ''}
                onChange={(e) => updateTranche(idx, 'justificatif', e.target.value)}
                rows={2}
                placeholder="Préciser la part de gaz pour le 2ème usage et la justification (procédé, matière première, etc.)"
                className={`w-full px-2 py-1 border rounded text-xs ${tranche.justificatif?.trim() ? 'border-[#BFC6CC]' : 'border-red-300 bg-red-50'}`}
                data-testid={`tranche-justificatif-${idx}`}
              />
            </div>
          )}
          {motifNeedsIcpe && (
            <div className="pl-1" data-testid={`tranche-icpe-block-${idx}`}>
              <label className="block text-xs font-medium text-[#687175] mb-0.5">
                Rubrique ICPE <span className="text-red-500">*</span>
                <span className="text-[#687175] italic font-normal ml-1">
                  (obligatoire — nomenclature produits minéraux non métalliques)
                </span>
              </label>
              <select
                value={tranche.icpe_rubrique || ''}
                onChange={(e) => updateTranche(idx, 'icpe_rubrique', e.target.value)}
                className={`w-full px-2 py-1 border rounded text-xs bg-white ${tranche.icpe_rubrique ? 'border-[#BFC6CC]' : 'border-red-300 bg-red-50'}`}
                data-testid={`tranche-icpe-${idx}`}
              >
                <option value="">— Sélectionner une rubrique ICPE —</option>
                {icpeRubriques.map(r => (
                  <option key={r.code} value={r.code}>
                    {r.code} — {r.libelle}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        );
      })}

      {/* Résumé */}
      {tranches.length > 0 && (
        <div className="flex items-center gap-2 text-xs">
          {totalParts > 100 && (
            <span className="text-red-600 font-medium">Total : {totalParts}% (dépasse 100%)</span>
          )}
          {restant > 0 && totalParts <= 100 && (
            <span className="text-[#687175]">{restant}% restant au taux normal</span>
          )}
          {restant === 0 && totalParts === 100 && (
            <span className="text-[#10D183] font-medium">100% réparti</span>
          )}
        </div>
      )}

      {/* Bouton ajouter */}
      {tousLesMotifs.length > 0 && (
        <button
          type="button"
          onClick={addTrancheLocal}
          disabled={totalParts >= 100}
          className="flex items-center gap-1 text-xs text-[#397DFF] hover:text-[#2563EB] disabled:text-[#BFC6CC] disabled:cursor-not-allowed"
          data-testid="add-tranche-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          Ajouter une tranche d'éligibilité
        </button>
      )}
    </div>
  );
};

export default TranchesEligibilite;
